import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Text,
  Container,
  Heading,
  VStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useBreakpointValue,
} from '@chakra-ui/react'

interface FAQItem {
  question: string
  answer: string
}

interface FAQCategory {
  title: string
  items: FAQItem[]
}

const FAQ: React.FC = () => {
  const { t } = useTranslation()
  const isMobile = useBreakpointValue({ base: true, md: false })

  const faqData: FAQCategory[] = [
    {
      title: '경매 관련',
      items: [
        {
          question: '경매 절차는 어떻게 진행되나요?',
          answer: `① 법원 경매 공고 확인
② 물건 정보 분석 (등기부, 현황조사, 권리관계)
③ 입찰 준비 (보증금·서류)
④ 입찰 참여 및 가격 제시
⑤ 최고가 낙찰자 선정 → 법원 낙찰 결정
⑥ 잔금 납부 후 소유권 이전 완료`
        },
        {
          question: '입찰 보증금은 얼마인가요?',
          answer: '감정가의 약 10%이며, 현금 또는 보증보험증권으로 납부합니다.'
        },
        {
          question: '낙찰 후 소유권 이전까지 기간은?',
          answer: '평균 4~6주 소요되며, 권리관계 복잡도에 따라 달라질 수 있습니다.'
        },
        {
          question: '유치권이 걸린 물건은 어떻게 하나요?',
          answer: '권리 해소 협의나 법원 판결이 필요하며, 전문가 자문을 권장합니다.'
        },
        {
          question: '3회 이상 유찰된 물건은 왜 기회인가요?',
          answer: '낙찰가가 감정가의 20~30%까지 떨어져 투자 매력이 높아집니다.'
        }
      ]
    },
    {
      title: '멤버십/팀 운영',
      items: [
        {
          question: '멤버십 운영 방식은?',
          answer: `회원가입 → 지역 배정 신청 → 지역장 승인
지역 공유자료/투자정보 접근 가능
지역 전용 경매물건·프랜차이즈 자료 열람
정기 성과 공유 및 전략 미팅 진행`
        },
        {
          question: '멤버십 가입 조건은?',
          answer: '만 19세 이상, 투자 의사가 있는 개인이라면 가능합니다.'
        },
        {
          question: '지역 배정은 어떻게 되나요?',
          answer: '가입 후 지역·투자 성향을 고려해 각 지역별 지역장이 승인합니다.'
        },
        {
          question: '지역장이 되려면?',
          answer: '일정 금액 이상 직접 투자 또는 추천 실적이 필요합니다.'
        },
        {
          question: '지역 내 주요 활동은?',
          answer: '투자 정보 공유, 현장 답사, 성과 발표, 내부 공지 관리 등이 있습니다.'
        },
        {
          question: '지역 탈퇴나 변경도 가능한가요?',
          answer: '네, 요청 시 관리자 확인 후 변경/탈퇴 가능합니다.'
        }
      ]
    },
    {
      title: '수익/투자 안정성',
      items: [
        {
          question: '수익 구조는 안정적인가요?',
          answer: `장기간 유찰된 물건을 저가 매입 후 권리 해결·개발을 통해 가치 상승
과거 사례 기준 평균 ROI 30% 이상 목표
확정 보장은 불가, 리스크(인허가 지연·시장 변동 등) 고려 필요`
        },
        {
          question: '투자금은 어떻게 보호되나요?',
          answer: '실물자산(토지·건물)에 기반해 운영되어 원금이 사라지지 않는 구조입니다.'
        },
        {
          question: '출구전략(Exit)은?',
          answer: '분양, 매각, 임대 운영 3가지 방식 중 선택합니다.'
        }
      ]
    },
    {
      title: '운영/서비스 이용',
      items: [
        {
          question: '회원 전용 자료는?',
          answer: '상세 경매물건 정보, ROI 데이터, 팀 공유자료 등.'
        },
        {
          question: '지역 전용 콘텐츠는?',
          answer: '현장 사진, 내부 리포트 등 심화 자료 제공.'
        }
      ]
    }
  ]

  return (
    <DefaultLayout>
      <Container maxW="container.xl" py={10}>
        <VStack spacing={8} align="stretch">
          <Box textAlign="center">
            <Heading
              as="h1"
              size={isMobile ? 'xl' : '2xl'}
              bgGradient="linear(to-r, brand.400, brand.600)"
              bgClip="text"
              fontWeight="bold"
              mb={4}
            >
              {t('FAQ')}
            </Heading>
            <Text fontSize="lg" color="gray.600">
              {t('자주 묻는 질문과 답변')}
            </Text>
          </Box>

          <VStack spacing={6} align="stretch">
            {faqData.map((category, categoryIndex) => (
              <Box
                key={categoryIndex}
                p={6}
                bg="white"
                borderRadius="lg"
                boxShadow="sm"
                border="1px solid"
                borderColor="gray.200"
              >
                <Heading size="md" mb={4} color="brand.600">
                  {category.title}
                </Heading>
                <Accordion allowToggle>
                  {category.items.map((item, itemIndex) => (
                    <AccordionItem key={itemIndex} border="none" mb={2}>
                      <AccordionButton
                        py={4}
                        px={4}
                        bg="gray.50"
                        borderRadius="md"
                        _hover={{ bg: 'gray.100' }}
                        _expanded={{ bg: 'brand.50', color: 'brand.600' }}
                      >
                        <Box flex="1" textAlign="left" fontWeight="medium">
                          {item.question}
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel
                        pb={4}
                        px={4}
                        pt={4}
                        whiteSpace="pre-wrap"
                        lineHeight="tall"
                        color="gray.700"
                      >
                        {item.answer}
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Box>
            ))}
          </VStack>
        </VStack>
      </Container>
    </DefaultLayout>
  )
}

export default FAQ

